/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getSites} from '@app/utils/site-utils'
import {urlPartPositions} from '@app/constants'

/**
 * Configures and returns a list of routes based on the provided configuration and site settings.
 *
 * @param {Array<Object>} routes - An array of route objects to be configured. Each route object should contain at least a `path` property.
 * @param {Object} config - Configuration object containing application settings, including URL configuration.
 * @param {Array<Object>} routesForOutlet - An array of route objects specifically for outlet sites.
 * @param {Object} options - Additional options for route configuration.
 * @param {Array<string>} options.ignoredRoutes - An array of route paths to be ignored during configuration.
 *
 * @returns {Array<Object>} - An array of configured route objects, each containing a `path` and additional route properties.
 */
export const configureRoutes = (routes = [], config, routesForOutlet, {ignoredRoutes = []}) => {
    if (!routes.length || !config) return routes

    const {url: urlConfig} = config.app
    const allSites = getSites()
    if (!allSites) return routes

    const outputRoutes = new Map()

    const addRoute = (path, routeRest) => {
        if (!outputRoutes.has(path)) {
            outputRoutes.set(path, {path, ...routeRest})
        }
    }

    routes.forEach(({path, ...routeRest}) => {
        if (ignoredRoutes.includes(path)) {
            addRoute(path, routeRest)
        } else {
            allSites.forEach((site) => {
                const selectedRoutes = site.id.includes('OUTLET') ? routesForOutlet : routes
                const siteRefs = [site.alias, site.id].filter(Boolean)
                const localeRefs = site.l10n.supportedLocales
                    .flatMap((locale) => [locale.alias, locale.id])
                    .filter(Boolean)

                selectedRoutes.forEach(({path: routePath, ...routeRest}) => {
                    const {locale: localePosition, site: sitePosition} = urlConfig

                    if (
                        localePosition === urlPartPositions.PATH &&
                        sitePosition === urlPartPositions.PATH
                    ) {
                        siteRefs.forEach((site) => {
                            addRoute(`/${site}${routePath}`, routeRest)
                            localeRefs.forEach((locale) => {
                                addRoute(`/${site}/${locale}${routePath}`, routeRest)
                                addRoute(`/${locale}${routePath}`, routeRest)
                            })
                        })
                    } else if (sitePosition === urlPartPositions.PATH) {
                        siteRefs.forEach((site) => addRoute(`/${site}${routePath}`, routeRest))
                    } else if (localePosition === urlPartPositions.PATH) {
                        localeRefs.forEach((locale) =>
                            addRoute(`/${locale}${routePath}`, routeRest)
                        )
                    }
                })
            })
            addRoute(path, routeRest)
        }
    })

    return Array.from(outputRoutes.values())
}
